import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Segmented, Select, DatePicker, Button, Tag, Empty } from 'antd';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  Tooltip, Legend, Filler,
} from 'chart.js';
import { useSelector } from 'react-redux';
import axios from 'axios';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeResponsiveTable, NdoeStats, NdoeStatCard,
  NdoeError, NdoeLoading,
} from '../../components/ndoe';
import { jetons, alpha, palette, optionsBase, fusionner } from '../../components/ndoe/chartTheme';
import { messageErreur } from '../../components/ndoe/chargement';
import CatalogueSelecteur from './CatalogueSelecteur';
import { money } from './format';
import './rapports.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const MAX_SERIES = 5;

const GRANULARITES = [
  { value: 'jour', label: 'Jour' },
  { value: 'semaine', label: 'Semaine' },
  { value: 'mois', label: 'Mois' },
  { value: 'trimestre', label: 'Trimestre' },
  { value: 'annee', label: 'Année' },
];

// Libellé lisible d'une période selon la granularité renvoyée par le serveur.
const MOIS_COURT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const libellePeriode = (cle, granularite) => {
  const s = String(cle);
  if (granularite === 'annee') return s;
  if (granularite === 'trimestre') return s.replace('-T', ' · T');
  if (granularite === 'mois') {
    const [a, m] = s.split('-');
    return `${MOIS_COURT[Number(m) - 1] || m} ${a.slice(2)}`;
  }
  const [, m, j] = s.split('-');
  const base = `${j}/${m}`;
  return granularite === 'semaine' ? `sem. ${base}` : base;
};

const jourStr = (d) => {
  const p = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Africa/Kinshasa', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  return p;
};
const ajouterJours = (iso, n) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Sélecteur d'une série : type (modèle/variante/hauteur) + articles cherchés. */
function ChoixSerie({ serie, couleur, niveaux, niveauxErr, onRetryNiveaux, onChange, onRetirer, onCatalogue }) {
  const estHauteur = serie.type === 'hauteur';
  // Les articles cochés dans le CATALOGUE n'ont jamais transité par la
  // recherche : sans leurs libellés, la liste afficherait des NUMÉROS.
  const [options, setOptions] = useState([]);
  const [cherche, setCherche] = useState(false);
  const [chercheErr, setChercheErr] = useState(null);
  const seq = useRef(0);

  // Changer de type (Modèle ↔ Variante) VIDE les propositions : sinon la liste
  // continuait d'offrir les ids de l'autre type — on envoyait un id de variante
  // dans une série « modèle ».
  useEffect(() => { setOptions([]); }, [serie.type]);

  const rechercher = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setOptions([]); setChercheErr(null); return; }
    const mien = ++seq.current;
    setCherche(true);
    setChercheErr(null);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/rapport/produits/evolution/recherche`, {
        params: { q, type: serie.type },
      });
      if (mien !== seq.current) return;
      setOptions((data.resultats || []).map((r) => ({
        value: r.id,
        label: r.label,
        title: r.detail || '',
      })));
    } catch (e) {
      if (mien === seq.current) { setOptions([]); setChercheErr(messageErreur(e, 'les résultats de la recherche')); }
    } finally {
      if (mien === seq.current) setCherche(false);
    }
  }, [serie.type]);

  // Options affichées = résultats de recherche + libellés déjà CONNUS des ids
  // sélectionnés (venus du catalogue), sans doublon.
  const optionsCompletes = useMemo(() => {
    const vus = new Set(options.map((o) => o.value));
    const connus = (serie.ids || [])
      .filter((id) => !vus.has(id))
      .map((id) => ({ value: id, label: (serie.libelles && serie.libelles[id]) || `#${id}` }));
    return [...options, ...connus];
  }, [options, serie.ids, serie.libelles]);

  return (
    <div className="ev-serie">
      <span className="ev-serie__puce" style={{ background: couleur || 'var(--ndoe-hairline, #ddd)' }} />
      <Segmented
        size="small"
        value={serie.type}
        onChange={(v) => onChange({ ...serie, type: v, ids: [], libelles: {} })}
        options={[
          { value: 'produit', label: 'Modèle' },
          { value: 'variante', label: 'Variante' },
          { value: 'hauteur', label: 'Talon' },
        ]}
      />
      <Select
        mode="multiple"
        showSearch={!estHauteur}
        className="ev-serie__select"
        placeholder={estHauteur ? 'Choisir un ou plusieurs niveaux de talon…'
          : serie.type === 'variante' ? 'Chercher une variante (nom ou code)…' : 'Chercher un modèle (nom ou code)…'}
        value={serie.ids}
        /* On mémorise aussi les libellés venus de la RECHERCHE : sinon, après
           un re-rendu, les articles choisis à la frappe redevenaient des « #id »
           (leurs options ne survivent pas à la fermeture de la liste). */
        onChange={(ids, options) => {
          const vus = Object.fromEntries((Array.isArray(options) ? options : [options])
            .filter(Boolean).map((o) => [o.value, o.label]));
          const libelles = {};
          ids.forEach((id) => {
            const l = vus[id] || (serie.libelles && serie.libelles[id]);
            if (l) libelles[id] = l;
          });
          onChange({ ...serie, ids, libelles });
        }}
        onSearch={estHauteur ? undefined : rechercher}
        filterOption={estHauteur}
        loading={cherche}
        // Type « Talon » : les 5 niveaux en liste fixe (pas de recherche).
        options={estHauteur
          ? niveaux.map((n) => ({ value: n.id, label: `${n.nom} (${n.fourchette})` }))
          : optionsCompletes}
        notFoundContent={estHauteur
          ? (niveauxErr
            ? <button type="button" className="ev-lien" onClick={onRetryNiveaux}>Échec du chargement — réessayer</button>
            : 'Chargement…')
          : (cherche ? 'Recherche…' : (chercheErr || 'Tapez au moins 2 lettres'))}
        maxTagCount={2}
      />
      {!estHauteur && (
        <Button size="small" onClick={onCatalogue} title="Choisir dans le catalogue (photos)">🖼️</Button>
      )}
      <Button size="small" type="text" danger onClick={onRetirer} title="Retirer cette série">✕</Button>
    </div>
  );
}

export default function EvolutionVentes() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  // uid stable par ligne : sans lui, supprimer une série transplantait l'état
  // local (propositions de recherche) de la ligne retirée sur sa suivante.
  const uidRef = useRef(1);
  const [series, setSeries] = useState([{ uid: 0, type: 'produit', ids: [] }]);
  const [granularite, setGranularite] = useState('mois');
  const [mode, setMode] = useState('comparer');
  const [fin, setFin] = useState(() => jourStr(new Date()));
  const [debut, setDebut] = useState(() => ajouterJours(jourStr(new Date()), -364));
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ongletDetail, setOngletDetail] = useState(0);
  const [avis, setAvis] = useState('');
  // Les 5 niveaux de talon (liste fixe) : servent le type de série « Hauteur »
  // et le filtre du catalogue. `niveauxErr` distingue « en cours » de « échec »
  // pour ne pas laisser la liste bloquée sur « Chargement… » si l'appel rate ;
  // un clic sur le type « Talon » relance alors la tentative.
  const [niveaux, setNiveaux] = useState([]);
  const [niveauxErr, setNiveauxErr] = useState(false);
  const chargerNiveaux = useCallback(() => {
    setNiveauxErr(false);
    axios.get(`${DOMAIN}/api/produit/hauteur-talon/niveaux`)
      .then(({ data }) => setNiveaux(data.niveaux || []))
      .catch(() => setNiveauxErr(true));
  }, []);
  useEffect(() => { chargerNiveaux(); }, [chargerNiveaux]);
  // Mode catalogue : sélection visuelle multiple (demande proprio 20/07).
  // cible = null → les articles créent de NOUVELLES séries ; cible = uid → ils
  // remplissent la série existante (regroupement).
  const [catalogue, setCatalogue] = useState(null);   // null | { cible }

  // Retour du catalogue : soit on remplit la série visée, soit on ajoute des
  // séries (en respectant le plafond et en réutilisant une ligne vide).
  const appliquerCatalogue = (nouvelles, libelles = {}) => {
    setSeries((arr) => {
      const cible = catalogue && catalogue.cible;
      if (cible !== null && cible !== undefined) {
        const ids = nouvelles.flatMap((n) => n.ids);
        return arr.map((x) => {
          if (x.uid !== cible) return x;
          const memeType = x.type === nouvelles[0].type;
          return {
            ...x,
            type: nouvelles[0].type,
            ids: [...new Set([...(memeType ? x.ids : []), ...ids])],
            libelles: { ...(memeType ? x.libelles : {}), ...libelles },
          };
        });
      }
      const vides = arr.filter((x) => !x.ids.length);
      const pleines = arr.filter((x) => x.ids.length);
      const ajoutees = nouvelles.slice(0, MAX_SERIES - pleines.length)
        .map((n, i) => ({
          uid: vides[i] ? vides[i].uid : uidRef.current++,
          type: n.type,
          ids: n.ids,
          libelles: Object.fromEntries(n.ids.map((id) => [id, libelles[id]]).filter(([, l]) => l)),
        }));
      const restantesVides = vides.slice(ajoutees.length);
      const total = [...pleines, ...ajoutees, ...restantesVides].slice(0, MAX_SERIES);
      return total.length ? total : [{ uid: uidRef.current++, type: 'produit', ids: [] }];
    });
  };

  const seriesPretes = useMemo(() => series.filter((s) => s.ids.length > 0), [series]);
  // SIGNATURE de la requête : ajouter une ligne vide, changer son type ou la
  // supprimer ne modifie PAS le résultat — sans cette signature, chaque
  // manipulation relançait un appel réseau inutile.
  const signature = useMemo(
    () => JSON.stringify(seriesPretes.map((s) => ({ t: s.type, i: [...s.ids].sort((a, b) => a - b) }))),
    [seriesPretes],
  );
  const reqSeq = useRef(0);

  const charger = useCallback(async () => {
    // On invalide les requêtes en vol : sinon celle lancée avant l'effacement
    // de la sélection revenait en erreur et affichait un message fantôme.
    // La signature EST la source de vérité (« [] » = aucune série prête) :
    // la lire ici garde la fonction cohérente avec ses dépendances.
    if (signature === '[]') { reqSeq.current += 1; setPayload(null); setError(''); setLoading(false); return; }
    const mien = ++reqSeq.current;
    setLoading(true);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/rapport/produits/evolution`, {
        params: {
          series: JSON.stringify(JSON.parse(signature).map((s) => ({ type: s.t, ids: s.i }))),
          granularite, mode, start_date: debut, end_date: fin,
        },
      });
      if (mien !== reqSeq.current) return;
      setPayload(data);
      setError('');
      setOngletDetail((o) => Math.min(o, (data.series || []).length - 1));
    } catch (e) {
      if (mien !== reqSeq.current) return;
      setPayload(null);
      setError(e.response?.data?.error || "Impossible de charger l'évolution.");
    } finally {
      if (mien === reqSeq.current) setLoading(false);
    }
  }, [signature, granularite, mode, debut, fin]);

  useEffect(() => { if (isAdmin) charger(); }, [isAdmin, charger]);

  // --- Graphique --------------------------------------------------------------
  const chart = useMemo(() => {
    if (!payload) return null;
    const t = jetons();
    const COULEURS = palette();
    const affichees = payload.cumul ? [payload.cumul] : payload.series;
    const labels = payload.periodes.map((p) => libellePeriode(p, payload.meta.granularite));
    return {
      data: {
        labels,
        datasets: affichees.map((s, i) => ({
          label: s.label,
          data: s.points.map((p) => p.paires),
          borderColor: COULEURS[i % COULEURS.length],
          backgroundColor: alpha(COULEURS[i % COULEURS.length], affichees.length === 1 ? 0.12 : 0),
          fill: affichees.length === 1,
          borderWidth: 2,
          tension: 0.3,
          // Les points PARLENT : gros point rouge = période avec rupture,
          // gros point vert = arrivage. C'est là que se lit le « pourquoi ».
          pointRadius: s.points.map((p) => (p.jours_rupture > 0 || p.arrivage > 0 ? 6 : 3)),
          pointHoverRadius: 8,
          pointBackgroundColor: s.points.map((p) => (
            p.arrivage > 0 ? t.success || '#28784b'
              : p.jours_rupture > 0 ? t.danger || '#b74b49'
                : COULEURS[i % COULEURS.length]
          )),
          pointBorderColor: s.points.map((p) => (
            p.arrivage > 0 || p.jours_rupture > 0 ? t.paper || '#fff' : COULEURS[i % COULEURS.length]
          )),
          pointBorderWidth: 2,
        })),
      },
      options: fusionner(optionsBase(), {
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: true, position: 'bottom', labels: { color: t.muted, boxWidth: 12, font: { size: 11 } } },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const s = affichees[ctx.datasetIndex];
                const p = s.points[ctx.dataIndex];
                return ` ${s.label} : ${p.paires} paire(s) · ${money(p.ca)}`;
              },
              // Le contexte qui justifie le creux ou le pic.
              afterBody: (items) => {
                const notes = [];
                items.forEach((it) => {
                  const p = affichees[it.datasetIndex].points[it.dataIndex];
                  if (p.arrivage > 0) notes.push(`▲ arrivage : +${p.arrivage} paire(s) reçue(s)`);
                  if (p.jours_rupture > 0) notes.push(`▼ rupture : ${p.jours_rupture} jour(s) sans stock`);
                  if (p.variation_ca !== null) notes.push(`${p.variation_ca >= 0 ? '▲' : '▼'} ${Math.abs(p.variation_ca)} % vs période précédente`);
                });
                return notes;
              },
            },
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: t.muted, font: { size: 11 }, maxRotation: 0, autoSkip: true } },
          y: {
            beginAtZero: true,
            grid: { color: t.hairline, drawTicks: false },
            ticks: { color: t.muted, font: { size: 11 }, precision: 0 },
            border: { display: false },
            title: { display: true, text: 'Paires vendues', color: t.muted, font: { size: 11 } },
          },
        },
      }),
    };
  }, [payload]);

  // --- Tableau résumé ----------------------------------------------------------
  const serieDetail = payload ? (payload.cumul || payload.series[ongletDetail] || payload.series[0]) : null;
  const colonnes = [
    { title: 'Période', dataIndex: 'periode', key: 'p',
      render: (v) => <b>{libellePeriode(v, payload?.meta?.granularite)}</b> },
    { title: 'Paires', dataIndex: 'paires', key: 'q', align: 'right', render: (v) => <span className="num">{v}</span> },
    { title: 'CA', dataIndex: 'ca', key: 'ca', align: 'right', render: (v) => <span className="num">{money(v)}</span> },
    { title: 'Évolution', dataIndex: 'variation_ca', key: 'var', align: 'right',
      render: (v) => (v === null || v === undefined
        ? <span className="ev-muted">—</span>
        : <b className={`num ${v >= 0 ? 'ev-hausse' : 'ev-baisse'}`}>{v >= 0 ? '▲' : '▼'} {Math.abs(v)} %</b>) },
    { title: 'Contexte', key: 'ctx', render: (t_, r) => (
      <div className="ev-chips">
        {r.arrivage > 0 && <Tag color="green">▲ arrivage +{r.arrivage}</Tag>}
        {r.jours_rupture > 0 && <Tag color="volcano">▼ rupture {r.jours_rupture} j</Tag>}
        {!r.arrivage && !r.jours_rupture && <span className="ev-muted">—</span>}
      </div>
    ) },
  ];

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Rapports" title="Évolution des ventes" />
        <NdoeError title="Accès réservé" text="Ce rapport est réservé à l'administration." />
      </NdoePage>
    );
  }

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Évolution des ventes"
        subtitle="Comparez jusqu'à 5 modèles ou variantes dans le temps — et voyez d'un coup d'œil si un creux vient d'une rupture ou un pic d'un arrivage."
      />

      {/* ── Paramétrage ────────────────────────────────────────────────── */}
      <div className="ndoe-card ev-panneau">
        <div className="ev-series">
          {series.map((s, i) => (
            <ChoixSerie
              key={s.uid}
              serie={s}
              niveaux={niveaux}
              niveauxErr={niveauxErr}
              onRetryNiveaux={chargerNiveaux}
              couleur={s.ids.length ? palette()[seriesPretes.indexOf(s) % palette().length] : null}
              onChange={(next) => setSeries((arr) => arr.map((x, j) => (j === i ? next : x)))}
              onCatalogue={() => setCatalogue({ cible: s.uid, type: s.type })}
              onRetirer={() => setSeries((arr) => (arr.length > 1 ? arr.filter((_, j) => j !== i) : [{ uid: uidRef.current++, type: 'produit', ids: [] }]))}
            />
          ))}
          <div className="ev-actions-series">
            <Button size="small" type="primary" ghost
              /* Sans place libre, ce bouton ne menait nulle part : on l'éteint
                 et on dit quoi faire (enrichir une ligne, ou en retirer une). */
              disabled={seriesPretes.length >= MAX_SERIES}
              title={seriesPretes.length >= MAX_SERIES
                ? MAX_SERIES + ' séries déjà tracées — retirez-en une, ou utilisez le bouton 🖼️ d’une ligne pour l’enrichir.'
                : 'Choisir des articles avec les photos'}
              onClick={() => setCatalogue({ cible: null })}>
              🖼️ Parcourir le catalogue
            </Button>
            {series.length < MAX_SERIES && (
              <Button size="small" type="dashed" onClick={() => setSeries((arr) => [...arr, { uid: uidRef.current++, type: 'produit', ids: [] }])}>
                + Série vide ({series.length}/{MAX_SERIES})
              </Button>
            )}
          </div>
          <p className="ev-aide">
            Une série = un modèle, une variante précise, <b>plusieurs articles regroupés</b>, ou
            une <b>hauteur de talon</b> entière (« Talon » = comparez vos talons hauts vs plats).
            Le <b>catalogue</b> permet de cocher plusieurs articles d'un coup, avec les photos.
          </p>
        </div>

        <div className="ev-reglages">
          <Segmented
            value={granularite}
            /* La vue « jour » est bornée à 31 jours côté serveur : au lieu de
               laisser l'écran tomber en erreur, on RESSERRE la fenêtre sur les
               30 derniers jours et on le dit. */
            onChange={(v) => {
              if (v === 'jour') {
                const joursFenetre = Math.round(
                  (new Date(`${fin}T12:00:00Z`) - new Date(`${debut}T12:00:00Z`)) / 86400000,
                ) + 1;
                if (joursFenetre > 31) {
                  setDebut(ajouterJours(fin, -29));
                  setAvis('Vue « jour » : fenêtre resserrée sur les 30 derniers jours.');
                } else setAvis('');
              } else setAvis('');
              setGranularite(v);
            }}
            options={GRANULARITES}
          />
          <DatePicker.RangePicker
            /* Non contrôlé, comme NdoePeriodPicker : avec value={null} le champ
               restait vide APRÈS la sélection (mode contrôlé permanent). */
            allowClear={false}
            format="DD/MM/YYYY"
            placeholder={[debut.split('-').reverse().join('/'), fin.split('-').reverse().join('/')]}
            onChange={(vals) => {
              if (!vals || !vals[0] || !vals[1]) return;
              setDebut(vals[0].format('YYYY-MM-DD'));
              setFin(vals[1].format('YYYY-MM-DD'));
            }}
          />
          {seriesPretes.length > 1 && (
            <Segmented value={mode} onChange={setMode} options={[
              { value: 'comparer', label: 'Comparer' },
              { value: 'cumuler', label: 'Cumuler' },
            ]} />
          )}
        </div>
        {avis && <p className="ev-avis">{avis}</p>}
      </div>

      {/* ── Résultat ───────────────────────────────────────────────────── */}
      {error ? (
        <NdoeError title="Erreur" text={error} onRetry={charger} />
      ) : !seriesPretes.length ? (
        <div className="ndoe-card ev-vide">
          <Empty description="Choisissez au moins un modèle ou une variante pour tracer l'évolution." />
        </div>
      ) : loading ? <NdoeLoading rows={5} /> : !payload ? null : (
        <>
          <NdoeStats>
            {(payload.cumul ? [payload.cumul] : payload.series).slice(0, 4).map((s, i) => (
              <NdoeStatCard key={i} label={s.label} gold={i === 0}
                value={`${s.total.paires} paires · ${money(s.total.ca)}`} />
            ))}
          </NdoeStats>

          <div className="ndoe-card ev-bloc">
            <div className="ev-graph">{chart && <Line data={chart.data} options={chart.options} />}</div>
            <p className="ev-legende">
              <Tag color="green">▲ arrivage</Tag> paires reçues sur la période ·
              <Tag color="volcano">▼ rupture</Tag> jours sans stock (estimation) — de quoi lire si un creux est <b>subi</b> ou <b>commercial</b>.
            </p>
          </div>

          {payload.series.length > 1 && !payload.cumul && (
            <div className="ev-onglets">
              {payload.series.map((s, i) => (
                <button key={i} type="button" className={i === ongletDetail ? 'is-actif' : ''}
                  onClick={() => setOngletDetail(i)}>
                  <span className="ev-onglets__puce" style={{ background: palette()[i % palette().length] }} />
                  {s.label}
                </button>
              ))}
            </div>
          )}

          <div className="ndoe-card ev-bloc">
            <NdoeResponsiveTable
              data={serieDetail ? serieDetail.points : []}
              columns={colonnes}
              rowKey="periode"
              pagination={false}
              scroll={{ x: 'max-content' }}
              empty={{ icon: '📈', title: 'Aucune période' }}
            />
          </div>

          <p className="ev-note">
            Ventes <b>confirmées</b> (une paire livrée et gardée), par {payload.meta.granularite_label.toLowerCase()}.
            Les jours de rupture sont <b>estimés</b> en remontant le grand livre des stocks depuis aujourd'hui ;
            un jour où une vente a été enregistrée n'est jamais compté comme rupture.
          </p>
        </>
      )}

      <CatalogueSelecteur
        open={!!catalogue}
        onClose={() => setCatalogue(null)}
        /* Remplir une ligne existante = UNE série (regroupement imposé, et la
           fenêtre s'ouvre sur le type de cette ligne). */
        typeInitial={(catalogue && catalogue.type) || 'produit'}
        imposerRegroupement={!!(catalogue && catalogue.cible != null)}
        placesLibres={catalogue && catalogue.cible != null
          ? 1
          : Math.max(0, MAX_SERIES - series.filter((x) => x.ids.length).length)}
        onValider={appliquerCatalogue}
      />
    </NdoePage>
  );
}
